import { useState, useRef, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Send, ChevronDown } from 'lucide-react'
import { Agent } from './AgentSelector'
import { useTranslation } from 'react-i18next'
import { getCurrentDirection } from '@/i18n/config'
import ChatVoice from './ChatInterface/ChatVoice'
import TooltipOverlay from './TooltipOverlay'
import { AZURE_CONFIG } from './ChatInterface'
import { AGENTS, AGENT_COLORS } from '@/features/chatV2/constants'
import type { AgentId } from '@/features/chatV2/types'
import { useAppStore } from '@/store'
import clsx from 'clsx'

// Mapping between chatV2 agent IDs and store agent IDs
const AGENT_ID_TO_STORE_ID: Record<AgentId, string> = {
  pam: 'plant-support',
  aice: 'chemical-agent',
  max: 'mechanical-agent',
  ella: 'electrical-agent',
}

const STORE_ID_TO_AGENT_ID: Record<string, AgentId> = {
  'plant-support': 'pam',
  'chemical-agent': 'aice',
  'mechanical-agent': 'max',
  'electrical-agent': 'ella',
}

function getStoreTitleForChatAgentId(
  agentId: AgentId,
  storeAgents: Agent[]
): string | undefined {
  const storeId = AGENT_ID_TO_STORE_ID[agentId]
  return storeAgents.find((a) => a.id === storeId)?.title
}

interface AIChatAgentProps {
  onSendMessage?: (message: string) => void
  selectedAgent?: Agent
  activeAgent?: AgentId
  onAgentChange?: (agentId: AgentId) => void
}

const AIChatAgent = ({ onSendMessage, selectedAgent, activeAgent: externalActiveAgent, onAgentChange }: AIChatAgentProps) => {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [showAgentDropdown, setShowAgentDropdown] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Get store state and actions
  const { agents: storeAgents, selectedAgent: storeSelectedAgent, setSelectedAgent } = useAppStore()

  // Derive activeAgent from store's selectedAgent or use external prop
  const activeAgent: AgentId = externalActiveAgent ??
    (storeSelectedAgent ? STORE_ID_TO_AGENT_ID[storeSelectedAgent.id] : 'pam') ??
    'pam'

  // Default agent if none provided
  const currentAgent = selectedAgent

  // Get current text direction for RTL support
  const isRTL = getCurrentDirection() === 'rtl'

  // Get active agent config and colors
  const agent = AGENTS[activeAgent]
  const colors = AGENT_COLORS[agent.color]
  const activeDisplayLabel =
    getStoreTitleForChatAgentId(activeAgent, storeAgents) ?? agent.label

  // Handle agent change - update store and call external handler if provided
  const handleAgentChange = useCallback((agentId: AgentId) => {
    // Update the store's selectedAgent
    const storeAgentId = AGENT_ID_TO_STORE_ID[agentId]
    const storeAgent = storeAgents.find(a => a.id === storeAgentId)
    if (storeAgent) {
      setSelectedAgent(storeAgent)
    }

    // Also call external handler if provided
    if (onAgentChange) {
      onAgentChange(agentId)
    }
  }, [onAgentChange, storeAgents, setSelectedAgent])

  // Use useCallback to prevent unnecessary re-renders
  const focusInput = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  const handleInput = useCallback(() => {
    const textarea = inputRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [])

  const handleSendMessage = useCallback(async () => {
    const trimmedMessage = message.trim()

    if (!trimmedMessage || !onSendMessage) return

    // Reset textarea height after sending
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
    }

    try {
      setMessage('')
      await onSendMessage(trimmedMessage)

      requestAnimationFrame(() => {
        focusInput()
      })
    } catch (error) {
      console.error('Failed to send message:', error)
      setMessage(trimmedMessage)
    }
  }, [message, onSendMessage, focusInput])

  const handleTranscript = useCallback(async (transcript: string) => {
    setMessage(transcript)

    requestAnimationFrame(() => {
      focusInput()
    })
  }, [focusInput])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }, [handleSendMessage])

  // Focus input on component mount
  useEffect(() => {
    focusInput()
  }, [focusInput])

  const canSend = message.trim().length > 0

  return (
    <div>
      <div className="relative w-full bg-stone-50 dark:bg-dark-muted rounded-2xl p-4 mt-4">
        {/* AI Agent Image */}
        {currentAgent && (
        <div className={`absolute ${isRTL ? 'left-4' : 'right-4'} bottom-0 z-10 group hidden md:block`}>
          <img
            src={currentAgent.image}
            alt={currentAgent.name}
            className="w-48 h-auto object-contain cursor-pointer"
          />
          {/* Tooltip */}
          <div className={`absolute bottom-full ${isRTL ? 'right-1/2 transform translate-x-1/2' : 'left-1/2 transform -translate-x-1/2'} mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap`}>
            {currentAgent.name}
            {/* Tooltip arrow */}
            <div className={`absolute top-full ${isRTL ? 'right-1/2 transform translate-x-1/2' : 'left-1/2 transform -translate-x-1/2'} w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900`}></div>
          </div>
        </div>
        )}

        {/* Chat Container */}
        <div className={`bg-white dark:bg-dark-card rounded-2xl border border-gray-200 dark:border-dark-border p-4 relative z-20 ${isRTL ? 'md:ml-56' : 'md:mr-56'}`}>
          {/* Input Field */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1">
              <textarea
                ref={inputRef}
                placeholder={t(`chat:input_placeholders.${activeAgent}`)}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                rows={1}
                className="w-full text-lg border-none focus-visible:ring-0 shadow-none resize-none bg-transparent outline-none"
              />
            </div>
          </div>

          {/* Bottom toolbar: Agent selector + Send button */}
          <div className="relative flex items-center">
            {/* Agent selector */}
            <div className="relative">
              <button
                onClick={() => setShowAgentDropdown(!showAgentDropdown)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
                  'text-sm font-medium',
                  colors.bg,
                  colors.text,
                  'hover:opacity-80 transition-opacity'
                )}
              >
                {activeDisplayLabel}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>

              {/* Agent dropdown */}
              {showAgentDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowAgentDropdown(false)}
                  />
                  <div className="absolute bottom-full left-0 mb-2 w-56 py-1 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-20">
                    {Object.values(AGENTS).filter((a) => {
                      if (!storeAgents || storeAgents.length === 0) return true
                      const storeId = AGENT_ID_TO_STORE_ID[a.id]
                      return !storeId || storeAgents.some((sa) => sa.id === storeId)
                    }).map((a) => {
                      const agentColors = AGENT_COLORS[a.color]
                      const rowLabel =
                        getStoreTitleForChatAgentId(a.id, storeAgents) ?? a.label
                      return (
                        <button
                          key={a.id}
                          onClick={() => {
                            handleAgentChange(a.id)
                            setShowAgentDropdown(false)
                          }}
                          className={clsx(
                            'w-full flex items-center gap-2 px-3 py-2 text-left',
                            'hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors',
                            activeAgent === a.id && 'bg-gray-50 dark:bg-dark-muted'
                          )}
                        >
                          <div className="relative w-6 h-6 rounded-full flex-shrink-0">
                            <img
                              src={a.avatarUrl}
                              alt={rowLabel}
                              className="w-6 h-6 rounded-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement
                                target.style.display = 'none'
                                const fallback = target.nextElementSibling as HTMLElement
                                if (fallback) fallback.classList.remove('hidden')
                              }}
                            />
                            {/* Fallback letter avatar */}
                            <div
                              className={clsx(
                                'hidden w-6 h-6 rounded-full flex items-center justify-center',
                                'text-xs font-bold',
                                agentColors.bg,
                                agentColors.text
                              )}
                            >
                              {rowLabel.charAt(0)}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-dark-text">
                              {rowLabel}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Mic and Send button */}
            <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 flex items-center gap-2`}>
              <ChatVoice
                onTranscript={handleTranscript}
                handleSendMessage={handleSendMessage}
                isRecording={isRecording}
                setIsRecording={setIsRecording}
                useAzure={!!AZURE_CONFIG.subscriptionKey}
                azureConfig={AZURE_CONFIG}
                borderless
              />
              <TooltipOverlay message={t('chat:send_message')}>
                <div className="inline-block">
                  <Button
                    onClick={handleSendMessage}
                    size="sm"
                    disabled={!canSend}
                    className="bg-orange-500 hover:bg-orange-600 disabled:cursor-not-allowed text-white p-2 rounded-full transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </TooltipOverlay>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AIChatAgent
